import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewColor = forwardRef(({ colorId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onColorCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [color, setColor] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (colorId && !createMode) {
      fetchColor();
    } else if (createMode) {
      // Initialize with default values for new color
      const newColor = {
        color_name: '',
        hex_value: '',
        pantone_code: '',
        is_active: true
      };
      setColor(newColor);
      setEditData(newColor);
    }
  }, [colorId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchColor = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/colors/${colorId}`));
      if (response.ok) {
        const data = await response.json();
        setColor(data);
        setEditData(data);
      } else {
        setError(t('colors.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching color:', error);
      setError(t('colors.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!editData.color_name) {
        showError(t('colors.view.validation.required'));
        return;
      }

      // Prepare data for API
      const saveData = {
        color_name: editData.color_name,
        hex_value: editData.hex_value,
        pantone_code: editData.pantone_code,
        is_active: editData.is_active
      };

      const url = createMode
        ? getApiUrl('/api/colors')
        : getApiUrl(`/api/colors/${colorId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedColor = await response.json();
        setColor(updatedColor);
        setEditData(updatedColor);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('colors.view.messages.createdSuccess'));
          if (onColorCreated) {
            onColorCreated(updatedColor.color_id);
          }
        } else {
          showSuccess(t('colors.view.messages.updatedSuccess'));
        }

        // Call onSave callback if provided
        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving color:', error);
      showError(tCommon('errors.genericTitle'), t('colors.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('colors.view.confirmations.unsavedChanges'))) {
        setEditData(color || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('colors.view.confirmations.deleteConfirm', { name: color.color_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/colors/${colorId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('colors.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting color:', error);
      showError(tCommon('errors.genericTitle'), t('colors.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('colors.loading')}</div></div>;
  }

  if (error && !color) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred while loading the color';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!color && !createMode) {
    return <div className="view-container"><div className="no-data">{t('colors.view.titles.noColorFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('colors.view.titles.newColor') : color?.color_name || t('colors.view.titles.viewColor')}
          </h2>
          {!createMode && color?.hex_value && (
            <span className="header-id">
              <span className="color-swatch" style={{ backgroundColor: color.hex_value || '#ccc', display: 'inline-block', width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle', border: '1px solid rgba(0,0,0,0.2)' }}></span>
              {color.hex_value}
            </span>
          )}
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('colors.filters.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('colors.filters.status.inactive')}</span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('colors.view.sections.colorInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('colors.view.fields.name')} *</label>
              <input
                type="text"
                value={editData.color_name || ''}
                onChange={(e) => handleFieldChange('color_name', e.target.value)}
                disabled={!editMode && !createMode}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('colors.view.fields.hexValue')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={editData.hex_value || ''}
                  onChange={(e) => handleFieldChange('hex_value', e.target.value)}
                  disabled={!editMode && !createMode}
                  placeholder="#000000"
                  maxLength="7"
                />
                <input
                  type="color"
                  value={editData.hex_value || '#000000'}
                  onChange={(e) => handleFieldChange('hex_value', e.target.value)}
                  disabled={!editMode && !createMode}
                  style={{ width: '40px', height: '32px', padding: '2px', cursor: (editMode || createMode) ? 'pointer' : 'default' }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('colors.view.fields.pantoneCode')}</label>
              <input
                type="text"
                value={editData.pantone_code || ''}
                onChange={(e) => handleFieldChange('pantone_code', e.target.value)}
                disabled={!editMode && !createMode}
              />
            </div>
            <div className="form-group">
              <label>{t('colors.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={!editMode && !createMode}
              >
                <option value="active">{t('colors.filters.status.active')}</option>
                <option value="inactive">{t('colors.filters.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {color && color.created_date && (
          <div className="form-section">
            <h3>{t('colors.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('colors.view.fields.createdDate')}</label>
                <span>{formatAppDate(color.created_date, 'datetime')}</span>
              </div>
              {color.updated_date && (
                <div className="info-item">
                  <label>{t('colors.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(color.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewColor;
